import {
  ButtonField,
  ButtonGroup,
  ButtonGroupItem,
} from '@wk/components-react16';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { closeModal } from 'redux/modal/ModalSlice';
import CsbErrorBoundary from 'components/CsbErrorBoudary/CsbErrorBoundary';
import { MODAL_ACTIONS } from 'interfaces/modals/closeModalInfo.interface';
import {
  selectImportModalSelectedFile,
  clearImportModalSelectedFile,
  selectSelectedWorkflow,
  setSelectedWorkflowById,
} from 'redux/runOrchestrations/RunOrchestrationsSlice';
import { useImportWorkflowMutation } from 'api/workflows/workflows';
import { useEffect, useRef, useState } from 'react';
import {
  checkAndScrollToItem,
  showToastAndClose,
} from 'utils/commonFunctions/CommonFunctions';

const CancelImportFooter = (props: any) => {
  const dispatch = useAppDispatch();
  const modalInfo = useAppSelector((state) => state.modal);
  const selectedFileName = useAppSelector(selectImportModalSelectedFile);
  const [importWorkflow] = useImportWorkflowMutation();
  const selectedWorkflow = useAppSelector(selectSelectedWorkflow);
  const updatedWorkflowIdRef = useRef<number | null>(null);
  const uploadedNewContent = props.watch('content');
  const uploadedUpdateContent = props.watch('updateContent');
  const selectedTab = props.watch('selectedTab');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(
    selectedTab === 0 ? uploadedNewContent?.[0] : uploadedUpdateContent?.[0]
  );

  useEffect(() => {
    setSelectedFile(
      selectedTab === 0 ? uploadedNewContent?.[0] : uploadedUpdateContent?.[0]
    );
  }, [selectedTab, uploadedNewContent, uploadedUpdateContent]);

  const handleUserRequest = (action: MODAL_ACTIONS) => {
    if (action === MODAL_ACTIONS.cancel) {
      props.reset();
      dispatch(clearImportModalSelectedFile());
      dispatch(
        closeModal({
          id: modalInfo.id,
          action,
        })
      );
    }
  };

  const handleImportSuccess = (isUpdate: boolean) => {
    if (isUpdate) {
      showToastAndClose('success', 'informationToast', () => {}, dispatch, {
        text: 'File updated successfully',
      });
      const id = selectedWorkflow
        ? selectedWorkflow.id
        : updatedWorkflowIdRef.current;
      if (id) {
        checkAndScrollToItem(`#workflow-name-${id}-cell`);
        dispatch(setSelectedWorkflowById(id));
      }
    } else {
      showToastAndClose('success', 'informationToast', () => {}, dispatch, {
        text: 'File imported successfully',
      });
      if (updatedWorkflowIdRef.current) {
        checkAndScrollToItem(
          `#workflow-name-${updatedWorkflowIdRef.current}-cell`
        );
        dispatch(setSelectedWorkflowById(updatedWorkflowIdRef.current));
      }
    }
  };

  const onImport = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      const isUpdate = selectedTab === 1;
      setLoading(true);
      const response: any = await importWorkflow(selectedFile);
      if (response?.data) {
        handleImportSuccess(isUpdate);
      } else {
        showToastAndClose('error', 'informationToast', () => {}, dispatch, {
          text: `File upload error: ${
            response?.error?.data ?? response.error.data
          }`,
        });
      }
    } catch (error) {
      showToastAndClose('error', 'informationToast', () => {}, dispatch, {
        text: `File upload error: ${error ?? ''}`,
      });
    }
    setLoading(false);
    handleUserRequest(MODAL_ACTIONS.cancel);
  };

  return (
    <CsbErrorBoundary>
      <ButtonGroup mode="static">
        <ButtonGroupItem slot="buttonGroupItem">
          <ButtonField mode={'text'}>
            <button
              type="button"
              id="cancel"
              onClick={() => handleUserRequest(MODAL_ACTIONS.cancel)}
              data-testid="CancelButton"
              disabled={loading}
            >
              Cancel
            </button>
          </ButtonField>
        </ButtonGroupItem>
        <ButtonGroupItem slot="buttonGroupItem">
          <ButtonField mode={'default'}>
            <button
              type="button"
              data-testid="ImportButton"
              id="import"
              onClick={onImport}
              disabled={!selectedFile || !selectedFileName || loading}
            >
              Import
            </button>
          </ButtonField>
        </ButtonGroupItem>
      </ButtonGroup>
    </CsbErrorBoundary>
  );
};

export default CancelImportFooter;
